import { Cross1Icon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import {
  createContext,
  forwardRef,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import React from 'react';
import { useOnFocusOrClickOutside } from '../hooks/useOnFocusOrClickOutside';
import { cn } from '../lib';
import { Input } from './Input';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';

interface AutocompleteSelectProps extends PropsWithChildren {
  id?: string;
  className?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onBlur?: () => void;
  required?: boolean;
  value: string;
  placeholder?: string;
  'aria-describedby'?: string;
  'aria-required'?: boolean;
}
interface IAutocompleteContext {
  childrenCount: number;
  increaseChildrenCount: () => void;
  decreaseChildrenCount: () => void;
  onOptionSelected: () => void;
  onEscape: () => void;
}
const AutocompleteSelectContext = createContext<IAutocompleteContext>({
  childrenCount: 0,
  increaseChildrenCount: () => {},
  decreaseChildrenCount: () => {},
  onOptionSelected: () => {},
  onEscape: () => {},
});

interface AutocompleteSelectType extends React.ForwardRefExoticComponent<
  AutocompleteSelectProps & React.RefAttributes<HTMLInputElement>
> {
  Options: typeof AutocompleteSelectOptions;
  Option: typeof AutocompleteSelectOption;
}

const AutocompleteSelectComponent = (
  {
    id,
    className,
    disabled,
    children,
    onChange,
    onKeyDown,
    onBlur,
    required,
    value,
    placeholder,
    'aria-describedby': ariaDescribedBy,
    'aria-required': ariaRequired,
  }: PropsWithChildren<AutocompleteSelectProps>,
  ref: React.Ref<HTMLInputElement>
) => {
  const [opened, setOpened] = useState(false);
  const [childrenCount, setChildrenCount] = useState(0);
  const clickOutsideRef = useOnFocusOrClickOutside(() => setOpened(false));
  const innerRef = useRef<HTMLInputElement>(null);
  const increaseChildrenCount = useCallback(
    () => setChildrenCount((prev) => prev + 1),
    []
  );
  const decreaseChildrenCount = useCallback(
    () => setChildrenCount((prev) => prev - 1),
    []
  );
  const onOptionSelected = useCallback(() => {
    setOpened(false);
    innerRef.current?.focus();
  }, []);

  const handleEscape = useCallback(() => {
    if (value.length > 0) {
      onChange('');
      return;
    }
    setOpened(false);
    innerRef.current?.focus();
  }, [value, onChange]);

  const contextValue = useMemo(
    () => ({
      childrenCount,
      increaseChildrenCount,
      decreaseChildrenCount,
      onOptionSelected,
      onEscape: handleEscape,
    }),
    [
      childrenCount,
      handleEscape,
      onOptionSelected,
      increaseChildrenCount,
      decreaseChildrenCount,
    ]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!opened) {
        setOpened(true);
        return;
      }
      const firstOption = clickOutsideRef.current?.querySelector('li');
      firstOption?.focus();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      handleEscape();
    }
    onKeyDown?.(e);
  };

  const handleOnChange = (value: string) => {
    onChange(value);
    setOpened(true);
  };

  const handleClear = () => {
    onChange('');
    innerRef.current?.focus();
    setOpened(false);
  };

  const handleOnClick = () => {
    setOpened(true);
  };
  return (
    <AutocompleteSelectContext.Provider value={contextValue}>
      <div className={cn('relative', className)} ref={clickOutsideRef}>
        <div className="flex flex-row font-normal">
          <Input
            id={id}
            ref={mergeRefs([innerRef, ref])}
            className="whitespace-nowrap overflow-hidden overflow-ellipsis pr-5 bg-background text-foreground"
            type="text"
            disabled={disabled}
            required={required}
            onChange={(e) => {
              handleOnChange(e.target.value);
            }}
            onClick={handleOnClick}
            onKeyDown={handleKeyDown}
            onBlur={onBlur}
            value={value}
            autoComplete="off"
            placeholder={placeholder}
            aria-describedby={ariaDescribedBy}
            aria-required={ariaRequired}
          />
          {value ? (
            <button
              onClick={handleClear}
              aria-label="Clear selection"
              className="absolute right-2 top-3"
            >
              <Cross1Icon className="h-3 w-3" aria-hidden="true" />
            </button>
          ) : (
            <MagnifyingGlassIcon
              className="absolute right-2 top-3 text-foreground"
              aria-hidden="true"
            />
          )}
        </div>
        {opened && <div>{children}</div>}
      </div>
    </AutocompleteSelectContext.Provider>
  );
};

const AutocompleteSelect = forwardRef<
  HTMLInputElement,
  AutocompleteSelectProps
>(AutocompleteSelectComponent) as AutocompleteSelectType;

interface AutocompleteSelectOptionsProps extends PropsWithChildren {
  isLoading?: boolean;
}

function AutocompleteSelectOptions({
  children,
  isLoading,
}: AutocompleteSelectOptionsProps) {
  const containerRef = useRef<HTMLUListElement>(null);
  const [isScrolledToTop, setIsScrolledToTop] = useState(true);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  useEffect(() => {
    const containerRefCurrent = containerRef.current;
    function handleScroll() {
      if (!containerRefCurrent) return;
      const { scrollTop, scrollHeight, clientHeight } = containerRefCurrent;
      setIsScrolledToTop(scrollTop === 0);
      setIsScrolledToBottom(scrollTop === scrollHeight - clientHeight);
    }

    containerRefCurrent?.addEventListener('scroll', handleScroll);
    return () =>
      containerRefCurrent?.removeEventListener('scroll', handleScroll);
  }, []);

  const hasOptions = React.Children.count(children) > 0;
  const showScrollShadows = !isLoading && hasOptions;

  return (
    <div className={cn('absolute', 'mt-2', 'w-full', 'bg-transparent')}>
      <ul
        ref={containerRef}
        className={cn(
          'relative',
          'z-10',
          'flex',
          'flex-col',
          'bg-background',
          'text-foreground',
          'rounded-md',
          'max-h-48',
          'overflow-y-scroll',
          'scrollbar-hide',
          'ring-1',
          'ring-offset-background',
          'ring-ring',
          'focus-visible:outline-none',
          'focus-visible:ring-2',
          'focus-visible:ring-offset-background',
          'focus-visible:ring-ring'
        )}
      >
        {isLoading ? (
          <li className="text-sm font-normal p-3 m-1 text-muted-foreground">
            Searching...
          </li>
        ) : !hasOptions ? (
          <li className="text-sm font-normal p-3 m-1 text-muted-foreground">
            No results found
          </li>
        ) : (
          children
        )}
      </ul>
      {showScrollShadows && !isScrolledToTop && (
        <div
          className={cn(
            'absolute',
            'top-0',
            'left-0',
            'right-0',
            'h-5',
            'pointer-events-none',
            'bg-gradient-to-b',
            'from-accent',
            'to-transparent',
            'rounded-md'
          )}
        />
      )}
      {showScrollShadows && !isScrolledToBottom && (
        <div
          className={cn(
            'absolute',
            'bottom-0',
            'left-0',
            'right-0',
            'h-5',
            'pointer-events-none',
            'bg-gradient-to-t',
            'from-accent',
            'to-transparent',
            'rounded-md'
          )}
        />
      )}
    </div>
  );
}

AutocompleteSelectOptions.displayName = 'AutocompleteSelect.Options';
AutocompleteSelect.Options = AutocompleteSelectOptions;

interface AutocompleteSelectOptionProps {
  onSelect?: () => void;
  selected?: boolean;
  className?: string;
}

function AutocompleteSelectOption({
  children,
  onSelect,
  selected = false,
  className,
}: PropsWithChildren<AutocompleteSelectOptionProps>) {
  const {
    increaseChildrenCount,
    decreaseChildrenCount,
    onOptionSelected,
    onEscape,
  } = useContext(AutocompleteSelectContext);

  useEffect(() => {
    increaseChildrenCount();
    return () => decreaseChildrenCount();
  }, [increaseChildrenCount, decreaseChildrenCount]);

  const _onSelect = useCallback(() => {
    onOptionSelected();
    onSelect?.();
  }, [onOptionSelected, onSelect]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      _onSelect();
      e.preventDefault();
    }

    if (e.key === 'ArrowDown') {
      const nextOption = e.currentTarget.nextElementSibling as HTMLElement;
      nextOption?.focus();
      e.preventDefault();
    }
    if (e.key === 'ArrowUp') {
      const prevOption = e.currentTarget.previousElementSibling as HTMLElement;
      prevOption?.focus();
      e.preventDefault();
    }

    if (e.key === 'Escape') {
      onEscape();
    }
  };
  return (
    <li
      className={cn(
        'flex',
        'flex-row',
        'items-center',
        'hover:bg-accent',
        'rounded-md',
        'p-2',
        'cursor-pointer',
        'text-sm',
        'font-normal',
        'p-3',
        'm-1',
        'focus-visible:bg-accent',
        'focus-visible:outline-none',
        'focus-visible:ring-1',
        'focus-visible:ring-offset-background',
        'focus-visible:ring-accent',
        className
      )}
      role="option"
      aria-selected={selected}
      onClick={() => _onSelect?.()}
      onKeyDown={onKeyDown}
      tabIndex={0}
    >
      {children}
    </li>
  );
}

AutocompleteSelectOption.displayName = 'AutocompleteSelect.Option';
AutocompleteSelect.Option = AutocompleteSelectOption;

export { AutocompleteSelect };
